export const PASSWORD_RESET_RETURN_URL_PARAM = 'returnUrl'

const ALLOWED_NEO_ORIGIN_PATTERNS: ReadonlyArray<RegExp> = [
  /^https:\/\/devneo\.traba\.work$/,
  /^https:\/\/[a-zA-Z0-9_-]+\.devneo\.traba\.work$/,
  /^https:\/\/neo\.traba\.work$/,
  /^https:\/\/neo\.traba\.ai$/,
  /^https:\/\/neo-sandbox\.traba\.work$/,
  /^https:\/\/[a-zA-Z0-9_-]+\.neo-platform-dev\.pages\.dev$/,
  /^https:\/\/[a-zA-Z0-9_-]+\.neo-platform-demo\.pages\.dev$/,
  /^https:\/\/[a-zA-Z0-9_-]+\.neo-platform-prod\.pages\.dev$/,
  /^http:\/\/127\.0\.0\.1:\d+$/,
  /^http:\/\/localhost:\d+$/,
]

function isAllowedNeoOrigin(origin: string): boolean {
  return ALLOWED_NEO_ORIGIN_PATTERNS.some((pattern) => pattern.test(origin))
}

export function isAllowedPasswordResetReturnUrl(url: string): boolean {
  if (url.startsWith('/') && !url.startsWith('//')) {
    return true
  }

  try {
    const parsed = new URL(url)
    return isAllowedNeoOrigin(parsed.origin)
  } catch {
    return false
  }
}

export function getPasswordResetContinueUrl(
  businessAppOrigin: string,
  returnUrl?: string | null,
): string {
  const continueUrl = new URL('/auth/action', businessAppOrigin)
  if (returnUrl && isAllowedPasswordResetReturnUrl(returnUrl)) {
    continueUrl.searchParams.set(PASSWORD_RESET_RETURN_URL_PARAM, returnUrl)
  }
  return continueUrl.toString()
}

export function buildBusinessAppForgotPasswordUrl(
  businessAppOrigin: string,
  returnUrl: string,
): string {
  const url = new URL('/forgot-password', businessAppOrigin)
  if (isAllowedPasswordResetReturnUrl(returnUrl)) {
    url.searchParams.set(PASSWORD_RESET_RETURN_URL_PARAM, returnUrl)
  }
  return url.toString()
}

export function resolvePasswordResetLoginTarget(
  returnUrl: string | null | undefined,
  loginPath = '/login',
): string {
  if (!returnUrl || !isAllowedPasswordResetReturnUrl(returnUrl)) {
    return loginPath
  }

  if (!returnUrl.startsWith('/') || returnUrl.startsWith('//')) {
    return returnUrl
  }

  if (
    returnUrl === loginPath ||
    returnUrl.startsWith(`${loginPath}?`) ||
    returnUrl.startsWith(`${loginPath}#`)
  ) {
    return returnUrl
  }

  return appendPasswordResetReturnUrl(loginPath, returnUrl)
}

export function navigateToPasswordResetReturnTarget(
  target: string,
  navigate: (path: string) => void,
): void {
  if (target.startsWith('/')) {
    navigate(target)
    return
  }
  window.location.href = target
}

export function appendPasswordResetReturnUrl(
  path: string,
  returnUrl: string | null | undefined,
): string {
  if (!returnUrl || !isAllowedPasswordResetReturnUrl(returnUrl)) {
    return path
  }

  const separator = path.includes('?') ? '&' : '?'
  return `${path}${separator}${PASSWORD_RESET_RETURN_URL_PARAM}=${encodeURIComponent(returnUrl)}`
}

/**
 * Pull a nested `returnUrl` out of a Firebase `continueUrl`. The reset/verify
 * flows set `actionCodeSettings.url` to a business-app `/auth/action?returnUrl=...`
 * URL (see getPasswordResetContinueUrl), so the originating origin is carried
 * inside the continueUrl Firebase echoes back to the action handler.
 */
function extractNestedReturnUrl(continueUrl: string): string | null {
  try {
    const parsed = new URL(continueUrl)
    return parsed.searchParams.get(PASSWORD_RESET_RETURN_URL_PARAM)
  } catch {
    return null
  }
}

/**
 * Resolve where to send a user after a Firebase email action completes on the
 * hosted `/auth/action` page. Prefers an explicit top-level `returnUrl` param
 * (the reset-password convention), then falls back to Firebase's `continueUrl`
 * (or a returnUrl nested within it), and finally to the local login path. The
 * result is always validated through resolvePasswordResetLoginTarget.
 */
export function resolveAuthActionContinueTarget(
  params: {
    returnUrl?: string | null
    continueUrl?: string | null
  },
  loginPath = '/login',
): string {
  if (params.returnUrl) {
    return resolvePasswordResetLoginTarget(params.returnUrl, loginPath)
  }
  if (params.continueUrl) {
    const nested = extractNestedReturnUrl(params.continueUrl)
    return resolvePasswordResetLoginTarget(
      nested ?? params.continueUrl,
      loginPath,
    )
  }
  return loginPath
}
